import * as pdfjs from 'pdfjs-dist';
import pdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?url';

import { resolveTrainingFileType } from '../types/training';

const THUMBNAIL_MAX_WIDTH = 640;
const JPEG_QUALITY = 0.8;
const VIDEO_STEP_TIMEOUT_MS = 15 * 1000;

pdfjs.GlobalWorkerOptions.workerSrc = pdfWorker;

function waitForVideoEvent(
    video: HTMLVideoElement,
    eventName: 'loadeddata' | 'seeked',
    errorMessage: string,
    trigger?: () => void,
): Promise<void> {
    return new Promise<void>((resolve, reject) => {
        let settled = false;
        let timeoutId = 0;
        const controller = new AbortController();
        const finish = (settle: () => void) => {
            if (settled) {
                return;
            }
            settled = true;
            window.clearTimeout(timeoutId);
            controller.abort();
            settle();
        };
        timeoutId = window.setTimeout(() => {
            finish(() => {
                reject(new Error(`${errorMessage} (timed out)`));
            });
        }, VIDEO_STEP_TIMEOUT_MS);
        video.addEventListener(
            eventName,
            () => {
                finish(() => {
                    resolve();
                });
            },
            { once: true, signal: controller.signal },
        );
        video.addEventListener(
            'error',
            () => {
                finish(() => {
                    reject(new Error(errorMessage));
                });
            },
            { once: true, signal: controller.signal },
        );
        try {
            trigger?.();
        } catch (error) {
            finish(() => {
                reject(
                    error instanceof Error ? error : new Error(errorMessage),
                );
            });
        }
    });
}

function canvasToJpegBlob(canvas: HTMLCanvasElement): Promise<Blob | null> {
    return new Promise(resolve => {
        canvas.toBlob(
            blob => {
                resolve(blob);
            },
            'image/jpeg',
            JPEG_QUALITY,
        );
    });
}

function drawScaledCanvas(
    source: CanvasImageSource,
    sourceWidth: number,
    sourceHeight: number,
): HTMLCanvasElement {
    const width = Math.max(1, sourceWidth);
    const height = Math.max(1, sourceHeight);
    const scale = Math.min(1, THUMBNAIL_MAX_WIDTH / width);
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(width * scale));
    canvas.height = Math.max(1, Math.round(height * scale));
    const context = canvas.getContext('2d');
    if (!context) {
        throw new Error('Could not create canvas context');
    }
    context.drawImage(source, 0, 0, canvas.width, canvas.height);
    return canvas;
}

async function captureImageThumbnail(file: File): Promise<Blob | null> {
    const bitmap = await createImageBitmap(file);
    try {
        const canvas = drawScaledCanvas(bitmap, bitmap.width, bitmap.height);
        return await canvasToJpegBlob(canvas);
    } finally {
        bitmap.close();
    }
}

async function captureVideoThumbnail(file: File): Promise<Blob | null> {
    const objectUrl = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.muted = true;
    video.playsInline = true;
    video.preload = 'auto';

    try {
        await waitForVideoEvent(
            video,
            'loadeddata',
            'Could not load video',
            () => {
                video.src = objectUrl;
            },
        );
        const duration = Number.isFinite(video.duration) ? video.duration : 0;
        const target = duration > 1 ? 1 : Math.max(0, duration * 0.1);
        if (target > 0 && target !== video.currentTime) {
            await waitForVideoEvent(
                video,
                'seeked',
                'Could not seek video',
                () => {
                    video.currentTime = target;
                },
            );
        }
        const canvas = drawScaledCanvas(
            video,
            video.videoWidth,
            video.videoHeight,
        );
        return await canvasToJpegBlob(canvas);
    } finally {
        video.removeAttribute('src');
        video.load();
        URL.revokeObjectURL(objectUrl);
    }
}

async function capturePdfThumbnail(file: File): Promise<Blob | null> {
    const data = await file.arrayBuffer();
    const pdfDocument = await pdfjs.getDocument({ data }).promise;
    try {
        const page = await pdfDocument.getPage(1);
        const unscaled = page.getViewport({ scale: 1 });
        const scale = Math.min(1, THUMBNAIL_MAX_WIDTH / unscaled.width);
        const viewport = page.getViewport({ scale });
        const canvas = window.document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(viewport.width));
        canvas.height = Math.max(1, Math.round(viewport.height));
        const context = canvas.getContext('2d');
        if (!context) {
            throw new Error('Could not create canvas context');
        }
        await page.render({ canvasContext: context, viewport }).promise;
        return await canvasToJpegBlob(canvas);
    } finally {
        await pdfDocument.destroy();
    }
}

export async function captureTrainingThumbnail(
    file: File,
): Promise<Blob | null> {
    const mimeType = resolveTrainingFileType(file) ?? file.type;
    try {
        if (mimeType.startsWith('image/')) {
            return await captureImageThumbnail(file);
        }
        if (mimeType.startsWith('video/')) {
            return await captureVideoThumbnail(file);
        }
        if (mimeType === 'application/pdf') {
            return await capturePdfThumbnail(file);
        }
        return null;
    } catch {
        return null;
    }
}
